import React, { useState, useEffect, useCallback } from "react";
import {
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Clock, Coffee, LogIn, LogOut, ChevronDown } from "lucide-react";
import "../styles/EmployeeAttendance.css";
import apiClient from "../api/api";
import { DateTime } from "luxon";

export default function Attendance() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [adminSettings, setAdminSettings] = useState(null);

  const rowsPerPageOptions = [10, 20, 50];

  // const getLondonTime = (date = new Date()) =>
  //   new Date(date.toLocaleString("en-US", { timeZone: "Europe/London" }));

  const formatTime = (dateTime) => {
    if (!dateTime) return "--:--";

    try {
      const londonTime = DateTime.fromISO(dateTime, {
        zone: "Europe/London",
      });

      // Check if this is the auto clock-out sentinel value
      if (londonTime.year === 2000 && londonTime.month === 1 &&
          londonTime.day === 1 && londonTime.hour === 0 &&
          londonTime.minute === 0 && londonTime.second === 0) {
        return "--:--"; // Display for auto clock-out sessions
      }

      return londonTime.toFormat("HH:mm");
    } catch (err) {
      console.error("Time format error:", err);
      return "--:--";
    }
  };

  const timeToHours = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours + minutes / 60;
  };

  const calculateOvertime = (start, end, breakStart, breakEnd) => {
    if (!start || !end || !adminSettings) return "00:00";

    const totalHoursWorked =
      (new Date(end) - new Date(start)) / (1000 * 60 * 60);

    let breakHours = 0;
    if (breakStart && breakEnd) {
      breakHours =
        (new Date(breakEnd) - new Date(breakStart)) / (1000 * 60 * 60);
    }

    const netWorkedHours = totalHoursWorked - breakHours;
    const fullDayHours = timeToHours(adminSettings.full_day_hours);
    const overtimeHours =
      netWorkedHours > fullDayHours ? netWorkedHours - fullDayHours : 0;

    const totalMinutes = Math.floor(overtimeHours * 60);
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");

    return `${hours}:${minutes}`;
  };

  const calculateTotalWorked = (clockIn, clockOut, breakStart, breakEnd) => {
    if (!clockIn || !clockOut) return "--:--";

    const start = new Date(clockIn);
    const end = new Date(clockOut);
    let totalMinutes = (end - start) / 60000;

    if (breakStart && breakEnd) {
      const breakDurationMinutes = (new Date(breakEnd) - new Date(breakStart)) / 60000;
      totalMinutes -= breakDurationMinutes;
    }

    // Floor total worked time to nearest minute
    totalMinutes = Math.floor(totalMinutes);

  // Return "--:--" for negative values
  if (totalMinutes <= 0) {
    return "--:--";
  }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTotalHours = (start, end) => {
    if (!start || !end) return 0;
    const diff = new Date(end) - new Date(start);
    return Math.round((diff / (1000 * 60 * 60)) * 100) / 100;
  };
  
  const toMinutes = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const getWorkType = (start, end, breakIn, breakOut, userRole) => {
    if (userRole && userRole.toLowerCase() === 'outsource') return "outsource";

    if (!start || !end || !adminSettings) return "absent";

    const startTime = new Date(start);
    const endTime = new Date(end);

    if (endTime <= startTime) return "absent";

    // Calculate total working time in milliseconds
    let workDurationMs = endTime - startTime;

    // Calculate break duration if valid
    let breakDurationMs = 0;
    if (breakIn && breakOut) {
      const breakStart = new Date(breakIn);
      const breakEnd = new Date(breakOut);
      if (breakEnd > breakStart) {
        breakDurationMs = breakEnd - breakStart;
      }
    }

    // Subtract break duration
    const netWorkDurationMs = workDurationMs - breakDurationMs;
    const netWorkMinutes = Math.floor(netWorkDurationMs / (1000 * 60));

    // Convert admin settings to minutes
    const fullDayMinutes = toMinutes(adminSettings.full_day_hours);
    const halfDayMinutes = toMinutes(adminSettings.half_day_hours);
    const shortDayMinutes = toMinutes(adminSettings.short_day_hours);

    if (netWorkMinutes >= fullDayMinutes) return "fullDay";
    if (netWorkMinutes >= halfDayMinutes) return "halfDay";
    if (netWorkMinutes >= shortDayMinutes) return "shortDay";
    return "absent";
  };

  const fetchAdminSettings = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin-settings");
      setAdminSettings(res.data);
    } catch (err) {
      console.error("Error fetching admin settings:", err);
    }
  }, []);

  const calculateTotalBreakTime = (start, end) => {
    if (!start || !end) return "00:00";
    try {
      const startTime = new Date(start);
      const endTime = new Date(end);

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()))
        return "00:00";

      const diff = endTime - startTime;
      const totalMinutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    } catch (error) {
      console.error("Error calculating break time:", error);
      return "00:00";
    }
  };


  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get("/attendance");
      const todayStr = new Date().toISOString().split("T")[0];
      const filteredData = res.data.filter(
        (r) => r.date.split("T")[0] !== todayStr
      );
      const sortedData = filteredData.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      const formatted = sortedData.map((r, i) => {
        const clockInDt = r.clock_in ? DateTime.fromISO(r.clock_in, { zone: "UTC" }).setZone("Europe/London") : null;
        const clockOutDt = r.clock_out ? DateTime.fromISO(r.clock_out, { zone: "UTC" }).setZone("Europe/London") : null;
        const breakEndDt = r.break_end ? DateTime.fromISO(r.break_end, { zone: "UTC" }).setZone("Europe/London") : null;

        const adjustedClockIn = r.clock_in;
        const adjustedClockOut = r.clock_out;
        const adjustedBreakEnd = breakEndDt ? breakEndDt.plus({ minutes: 1 }).toISO() : null;

        const workType = getWorkType(
          adjustedClockIn,
          adjustedClockOut,
          r.break_start,
          r.break_end,
          r.user?.role
        );

        return {
          id: r.id || i + 1,
          date: DateTime.fromISO(r.date, { zone: "UTC" }).setZone("Europe/London").toFormat("dd/MM/yyyy"),
          rawDate: r.date,
          CheckIn: clockInDt ? clockInDt.toFormat("HH:mm") : "--:--",
          CheckOut: clockOutDt ? clockOutDt.toFormat("HH:mm") : "--:--",
          breakTime: calculateTotalBreakTime(r.break_start, r.break_end),
          workType,
          fullDay: workType === "fullDay",
          halfDay: workType === "halfDay",
          shortDay: workType === "shortDay",
          absent: workType === "absent",
          outsource: workType === "outsource",
          overtime: (r.user?.role && r.user.role.toLowerCase() === 'outsource') ? "--" : calculateOvertime(adjustedClockIn, adjustedClockOut, r.break_start, r.break_end),
          totalProductive: calculateTotalWorked(
            adjustedClockIn,
            adjustedClockOut,
            r.break_start,
            r.break_end
          ),
          totalHours: calculateTotalHours(adjustedClockIn, adjustedClockOut),
        };
      });
      setAttendanceRecords(formatted);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    }
  }, [currentTime, adminSettings]);

  useEffect(() => {
    fetchAdminSettings();
  }, [fetchAdminSettings]);

  useEffect(() => {
    if (adminSettings) {
      fetchData();
    }
  }, [fetchData, adminSettings]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (adminSettings) {
        fetchData();
      }
    }, 600000); // 10 minutes
    return () => clearInterval(timer);
  }, [fetchData, adminSettings]);

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const indexOfLastRecord = currentPage * rowsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage;
  const currentRecords = attendanceRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(attendanceRecords.length / rowsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="attendance-app">
      <div className="main-content">
        <div className="card attendance-records">
          <h2 className="card-title">Attendance Records</h2>
          <div className="records-table">
            {attendanceRecords.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>DATE</th>
                    <th>CHECK IN</th>
                    <th>CHECK OUT</th>
                    <th>BREAK DURATION</th>
                    <th>WORK TYPE</th>
                    <th>EXTRA TIME</th>
                    <th>TOTAL WORKED</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((record, index) => (
                    <tr key={`record-${record.id}`}>
                      <td className="index-cell">
                        {String(indexOfFirstRecord + index + 1).padStart(
                          2,
                          "0"
                        )}
                      </td>
                      <td className="date-cell">{record.date}</td>
                      <td>
                        <div className="time-cell">
                          <Clock size={14} className="time-icon" />
                          {record.CheckIn}
                        </div>
                      </td>
                      <td className="check-out-cell">
                        <div className="time-cell">
                          <Clock size={14} className="time-icon" />
                          {record.CheckOut}
                        </div>
                      </td>
                      <td>{record.breakTime}</td>
                      <td>
                        {record.outsource ? (
                          <span className="status-badge outsource">
                            Outsource
                          </span>
                        ) : record.fullDay ? (
                          <span className="status-badge full-day">
                            Full Day
                          </span>
                        ) : record.halfDay ? (
                          <span className="status-badge half-day">
                            Half Day
                          </span>
                        ) : record.shortDay ? (
                          <span className="status-badge short-day">
                            Short Day
                          </span>
                        ) : (
                          <span className="status-badge absent">Absent</span>
                        )}
                      </td>
                      <td className="overtime-cell">{record.outsource ? "--" : record.overtime}</td>
                      <td className="total-worked-cell">
                        {record.totalProductive}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <p>No attendance records found</p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {attendanceRecords.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <div className="rows-per-page-container">
                  <span>Rows per page:</span>
                  <div className="rows-per-page-dropdown">
                    <select
                      value={rowsPerPage}
                      onChange={(e) =>
                        handleRowsPerPageChange(Number(e.target.value))
                      }
                      className="rows-per-page-select"
                    >
                      {rowsPerPageOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="dropdown-icon" />
                  </div>
                </div>
                <span>
                  {indexOfFirstRecord + 1}-
                  {Math.min(indexOfLastRecord, attendanceRecords.length)} of{" "}
                  {attendanceRecords.length}
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`pagination-button ${
                        currentPage === number ? "active" : ""
                      }`}
                    >
                      {number}
                    </button>
                  )
                )}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
